/**
 * <spivex-cart-form>: progressive enhancement for small Spivex cart forms
 * (adapter from the vacuum check, greeting card, package upgrade in the cart).
 *
 * Without JavaScript, or when Shopify's standard actions are missing, the inner <form> posts normally
 * (/cart/add or /cart/update) and Shopify redirects to the cart page.
 * With window.Shopify.actions.updateCart the cart is changed in place. Horizon listens to the standard
 * cart events, re-renders the cart drawer and opens it after an add.
 *
 * Supported form fields:
 * - add:    id, quantity, properties[Name]   → one new line
 * - update: updates[lineKeyOrVariantId]=qty  → change/remove lines, add variants (used for upgrades)
 *
 * With data-mit-zusaetzen (set finder result) every ticked add-on of the section (snippets/spivex-zusatz: adapter,
 * greeting card with its text) goes into the same request; afterwards the ticks are cleared.
 */
class SpivexCartForm extends HTMLElement {
  // Spivex 28.10.2026: the submit is caught here on the element itself, not on the inner <form>. The cart drawer
  // swaps its content in place after every change (the <form> inside is then a new one); a listener on the old
  // form missed it and the shopper landed on the big cart page.
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.querySelector('form');
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const actions = window.Shopify?.actions;
    const form = event.target instanceof HTMLFormElement ? event.target : this.#form;
    if (!form) return;

    const lines = this.#lines(new FormData(form));
    if (!lines.length) return;
    // 29.10.2026: ohne Horizons Warenkorb-Funktionen (z. B. am Handy noch nicht geladen) ein reines Hinzufügen selbst
    // erledigen und mit offenem kleinen Warenkorb neu laden, statt zur großen Warenkorb-Seite zu springen
    if (!actions?.updateCart) {
      event.preventDefault();
      try {
        await SpivexCartForm.#selbstAusfuehren(lines);
      } catch {
        form.submit();
      }
      return;
    }
    const addOns = this.#addOns();
    for (const addOn of addOns) lines.push(addOn.line);

    event.preventDefault();
    const button = /** @type {HTMLButtonElement | null} */ (event.submitter || form.querySelector('[type="submit"]'));
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;

    try {
      const result = await actions.updateCart({ lines }, { event: { context: 'product' } });
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
      // Spivex 28.10.2026: a greeting card bought with the package names it ("Beilegen zu: Spivex Basis")
      const paketId = lines[0]?.merchandiseId;
      if (paketId && addOns.some(({ box }) => box.matches('[data-gk-box], .spivex-gift__box'))) {
        await this.#kartenZuordnen(actions, paketId);
      }
      for (const { box, fields } of addOns) {
        box.checked = false;
        fields?.reset();
        box.dispatchEvent(new Event('change', { bubbles: true }));
        // Grußkarten-Motivwahl setzt sich zurück, aufgeklappt bleibt sie (assets/spivex-grusskarten.js)
        box.dispatchEvent(new CustomEvent('spivex:zusatz-hinzugefuegt', { bubbles: true }));
      }
      this.#announce(this.dataset.successText);
      if (this.dataset.openCart === 'true') actions.openCart?.();
    } catch (error) {
      console.warn('[spivex-cart-form]', error);
      // 29.10.2026: Beim Hinzufügen aus dem leeren Warenkorb (Handy) kam der Fehler erst beim Neuaufbau des Warenkorbs,
      // die Ware lag da schon drin; das Formular schickte sie dann ein zweites Mal ab und führte zur großen
      // Warenkorb-Seite. Jetzt: liegt sie schon drin, Seite mit offenem kleinen Warenkorb neu laden; sonst selbst
      // hinzufügen; erst wenn auch das nicht geht, das normale Formular.
      // 29.10.2026 (5): auch Änderungen (Upgrade im Warenkorb) so selbst erledigen; in der Theme-Vorschau schlug
      // Horizons Warenkorb-Funktion fehl (SFAPI 404) und die Upgrade-Knöpfe führten zur großen Warenkorb-Seite
      try {
        if (SpivexCartForm.#nurHinzufuegen(lines) && (await SpivexCartForm.#liegtDrin(lines))) {
          SpivexCartForm.#neuMitWarenkorb();
        } else {
          await SpivexCartForm.#selbstAusfuehren(lines);
        }
        return;
      } catch {}
      // Never leave the shopper stuck: fall back to the normal form post.
      form.submit();
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };

  /**
   * @param {Array<{id?: string, merchandiseId?: string}>} lines
   * @returns {boolean} nur neue Artikel (keine Änderung bestehender Zeilen)
   */
  static #nurHinzufuegen(lines) {
    return lines.every((line) => line.merchandiseId && !line.id);
  }

  /** @param {Array<{merchandiseId?: string}>} lines */
  static async #liegtDrin(lines) {
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json());
    return lines.every((line) => cart.items?.some((item) => String(item.variant_id) === String(line.merchandiseId)));
  }

  /** @param {Array<{merchandiseId?: string, quantity: number, attributes?: Array<{key: string, value: string}>}>} lines */
  static async #selbstHinzufuegen(lines) {
    const items = lines.map((line) => ({
      id: Number(line.merchandiseId),
      quantity: line.quantity,
      properties: Object.fromEntries((line.attributes || []).map((a) => [a.key, a.value])),
    }));
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) throw new Error(`cart/add ${response.status}`);
    SpivexCartForm.#neuMitWarenkorb();
  }

  /**
   * Hinzufügen (cart/add.js) oder Ändern (cart/change.js für Zeilen mit Eigenschaften, cart/update.js für Mengen) ohne
   * Horizon, danach mit offenem kleinen Warenkorb neu laden. Mengen sind feste Werte, doppelt ausgeführt ändert nichts.
   * @param {Array<{id?: string, merchandiseId?: string, quantity: number, attributes?: Array<{key: string, value: string}>}>} lines
   */
  static async #selbstAusfuehren(lines) {
    if (SpivexCartForm.#nurHinzufuegen(lines)) {
      await SpivexCartForm.#selbstHinzufuegen(lines);
      return;
    }
    const root = window.Shopify?.routes?.root || '/';
    const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
    // erst Zeilen mit Eigenschaften (Grußkarte „Beilegen zu“), deren Schlüssel stimmen jetzt noch
    for (const line of lines) {
      if (!line.id || !line.attributes) continue;
      const properties = Object.fromEntries(line.attributes.map((a) => [a.key, a.value]));
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: json,
        body: JSON.stringify({ id: line.id, quantity: line.quantity, properties }),
      });
      if (!response.ok) throw new Error(`cart/change ${response.status}`);
    }
    /** @type {Record<string, number>} */
    const updates = {};
    for (const line of lines) {
      if (line.attributes && line.id) continue;
      const key = line.id || line.merchandiseId;
      if (key) updates[key] = line.quantity;
    }
    if (Object.keys(updates).length) {
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: json,
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`cart/update ${response.status}`);
    }
    SpivexCartForm.#neuMitWarenkorb();
  }

  /** Seite neu laden, danach öffnet snippets/spivex-warenkorb-kompakt den kleinen Warenkorb */
  static #neuMitWarenkorb() {
    try {
      sessionStorage.setItem('spivex-warenkorb-oeffnen', '1');
    } catch {}
    window.location.reload();
  }

  /**
   * @param {FormData} data
   * @returns {Array<{id?: string, merchandiseId?: string, quantity: number, attributes?: Array<{key: string, value: string}>}>}
   */
  #lines(data) {
    const updates = [];
    for (const [name, value] of data.entries()) {
      const match = name.match(/^updates\[(.+)\]$/);
      if (!match) continue;
      const key = match[1];
      const quantity = Number(value);
      // Line keys from the AJAX cart contain a colon; plain numbers are variant ids.
      updates.push(key.includes(':') ? { id: key, quantity } : { merchandiseId: key, quantity });
    }
    // Spivex 28.10.2026: further lines to change with the same request, as JSON {id, quantity, attributes}
    // (snippets/spivex-cart-upgrade: greeting cards move to the bigger package, "Beilegen zu").
    for (const raw of data.getAll('spx-zeile')) {
      try {
        updates.push(JSON.parse(String(raw)));
      } catch {}
    }
    if (updates.length) return updates;

    const variantId = data.get('id');
    if (!variantId) return [];
    const attributes = [];
    for (const [name, value] of data.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== '') attributes.push({ key: match[1], value: String(value).trim() });
    }
    const line = { merchandiseId: String(variantId), quantity: Number(data.get('quantity') || 1) };
    if (attributes.length) line.attributes = attributes;
    return [line];
  }

  /**
   * Ticked, visible add-ons of the section, each with its line (variant from its fields form or data-variant-id).
   * @returns {Array<{box: HTMLInputElement, fields: HTMLFormElement | null, line: {merchandiseId?: string, quantity: number, attributes?: Array<{key: string, value: string}>}}>}
   */
  #addOns() {
    if (!this.hasAttribute('data-mit-zusaetzen')) return [];
    const root = this.closest('.shopify-section') || document.body;
    const addOns = [];
    for (const box of root.querySelectorAll('input[data-spivex-zusatz]:checked')) {
      if (!(box instanceof HTMLInputElement) || box.closest('[hidden]')) continue;
      const element = box.dataset.spivexFelder ? document.getElementById(box.dataset.spivexFelder) : null;
      const fields = element instanceof HTMLFormElement ? element : null;
      const data = fields ? new FormData(fields) : new FormData();
      if (!data.get('id') && box.dataset.variantId) data.set('id', box.dataset.variantId);
      const [line] = this.#lines(data);
      if (line) addOns.push({ box, fields, line });
    }
    return addOns;
  }

  /**
   * Gives every greeting card line in the cart without "Beilegen zu" the name of the package just bought.
   * @param {{updateCart: Function}} actions
   * @param {string} paketId - variant id of the package
   */
  async #kartenZuordnen(actions, paketId) {
    try {
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json());
      const paket = cart.items?.find((item) => String(item.variant_id) === String(paketId));
      if (!paket?.product_title) return;
      const zeilen = cart.items
        .filter((item) => /geschenkkarte/.test(item.handle) && !item.properties?.['Beilegen zu'])
        .map((item) => ({
          id: item.key,
          quantity: item.quantity,
          attributes: [
            { key: 'Beilegen zu', value: paket.product_title },
            ...Object.entries(item.properties || {})
              .filter(([, value]) => value != null && String(value) !== '')
              .map(([key, value]) => ({ key, value: String(value) })),
          ],
        }));
      if (zeilen.length) await actions.updateCart({ lines: zeilen }, { event: { context: 'product' } });
    } catch (error) {
      console.warn('[spivex-cart-form] Grußkarte zuordnen', error);
    }
  }

  /** @param {string | undefined} text */
  #announce(text) {
    const region = this.querySelector('[data-spivex-cart-form-status]');
    if (region && text) region.textContent = text;
  }
}

if (!customElements.get('spivex-cart-form')) customElements.define('spivex-cart-form', SpivexCartForm);
